import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { PageHeader, StatusBadge, SecondaryButton, PrimaryButton } from "@/components/ui-parts";
import { ArrowLeft, Download, Link as LinkIcon, Unlink, Loader2, AlertTriangle, RefreshCw, Send, X } from "lucide-react";
import { toast } from "sonner";

export default function StatementDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unmatchedReceipts, setUnmatchedReceipts] = useState([]);
  const [assigning, setAssigning] = useState(null); // tx id being assigned
  const [reanalyzing, setReanalyzing] = useState(false);
  const [requestingTx, setRequestingTx] = useState(null); // {tx, members} for modal
  const [team, setTeam] = useState([]);
  const [requestNote, setRequestNote] = useState("");
  const [requestMember, setRequestMember] = useState("");
  const [pendingReqs, setPendingReqs] = useState({}); // tx_id -> request
  const [unmatchTarget, setUnmatchTarget] = useState(null); // {receipt_id, tx} for note modal
  const [unmatchNote, setUnmatchNote] = useState("");
  const [unmatchBusy, setUnmatchBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/statements/${id}`);
      setData(data);
      const [{ data: recs }, { data: t }, { data: pr }] = await Promise.all([
        api.get(`/receipts`, { params: { company_id: data.statement.company_id, unmatched_only: true } }),
        api.get(`/team`).catch(() => ({ data: [] })),
        api.get(`/receipt-requests`, { params: { status: "pending" } }).catch(() => ({ data: [] })),
      ]);
      setUnmatchedReceipts(recs);
      setTeam(t.filter((m) => !m.is_owner && m.telegram_linked));
      const map = {};
      (pr || []).forEach((r) => { map[r.transaction_id] = r; });
      setPendingReqs(map);
    } catch { toast.error("Auszug konnte nicht geladen werden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const assign = async (txId, receiptId) => {
    setAssigning(txId);
    try {
      await api.post("/match", { transaction_id: txId, receipt_id: receiptId });
      toast.success("Beleg zugeordnet");
      load();
    } catch { toast.error("Zuordnung fehlgeschlagen"); }
    finally { setAssigning(null); }
  };

  const openUnmatch = (tx) => {
    if (!tx?.matched_receipt) return;
    setUnmatchTarget({ receipt_id: tx.matched_receipt.id, tx });
    setUnmatchNote("");
  };

  const submitUnmatch = async () => {
    if (!unmatchTarget) return;
    setUnmatchBusy(true);
    try {
      await api.post("/unmatch", {
        receipt_id: unmatchTarget.receipt_id,
        note: unmatchNote.trim() || null,
      });
      toast.success(unmatchNote.trim()
        ? "Zuordnung aufgehoben · KI-Notiz gespeichert"
        : "Zuordnung aufgehoben");
      setUnmatchTarget(null);
      setUnmatchNote("");
      load();
    } catch {
      toast.error("Fehlgeschlagen");
    } finally {
      setUnmatchBusy(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const [exportPct, setExportPct] = useState(null);

  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    setExportPct(null);
    const toastId = toast.loading("PDF wird vorbereitet …");
    const t0 = Date.now();
    let lastLoaded = 0;
    try {
      const res = await api.get(`/statements/${id}/export`, {
        responseType: "blob",
        onDownloadProgress: (evt) => {
          const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
          if (evt.total && evt.total > 0) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setExportPct(pct);
            toast.loading(`PDF wird geladen … ${pct}% · ${elapsed}s`, { id: toastId });
          } else if (evt.loaded > lastLoaded) {
            lastLoaded = evt.loaded;
            const mb = (evt.loaded / 1024 / 1024).toFixed(1);
            toast.loading(`PDF wird erstellt … ${mb} MB · ${elapsed}s`, { id: toastId });
          }
        },
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vorbuch_${data.statement.name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF-Export abgeschlossen", { id: toastId });
    } catch {
      toast.error("PDF-Export fehlgeschlagen", { id: toastId });
    } finally {
      setExporting(false);
      setExportPct(null);
    }
  };

  const openRequest = (tx) => {
    setRequestingTx(tx);
    setRequestNote("");
    setRequestMember(team[0]?.id || "");
  };

  const submitRequest = async () => {
    if (!requestingTx || !requestMember) return;
    try {
      const { data } = await api.post("/receipt-requests", {
        transaction_id: requestingTx.id,
        member_id: requestMember,
        note: requestNote || null,
      });
      if (data?.telegram_delivered === false) {
        toast.warning("Anfrage gespeichert – Telegram-Zustellung fehlgeschlagen");
      } else {
        toast.success("Beleg-Anfrage per Telegram gesendet");
      }
      setRequestingTx(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Anfrage fehlgeschlagen: ${detail}`);
    }
  };

  const cancelRequest = async (reqId) => {
    try {
      await api.delete(`/receipt-requests/${reqId}`);
      toast.success("Anfrage abgebrochen");
      load();
    } catch {
      toast.error("Fehlgeschlagen");
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      const { data: res } = await api.post(`/statements/${id}/reanalyze`, {});
      toast.success(`Erneut analysiert · ${res.transaction_count} Buchungen erkannt · ${res.matched} zugeordnet`);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Neu-Analyse fehlgeschlagen: ${detail}`);
    } finally { setReanalyzing(false); }
  };

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Kontoauszug" />
        <div className="px-8 py-8 text-sm text-[#5C5E60]">Lade...</div>
      </div>
    );
  }

  const s = data.statement;
  const matchedCount = data.transactions.filter((t) => t.matched_receipt_id).length;

  return (
    <div>
      <PageHeader
        title={s.filename || "Kontoauszug"}
        subtitle={s.period_start ? `${fmtDate(s.period_start)} – ${fmtDate(s.period_end)} · IBAN ${s.iban || "—"}` : "Kontoauszug-Details"}
        actions={
          <>
            <Link to="/app/kontoauszuege">
              <SecondaryButton data-testid="back-to-statements">
                <ArrowLeft className="h-4 w-4" /> Zurück
              </SecondaryButton>
            </Link>
            <SecondaryButton onClick={reanalyze} disabled={reanalyzing} data-testid="reanalyze-btn">
              {reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Erneut analysieren
            </SecondaryButton>
            <PrimaryButton onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting
                ? (exportPct !== null ? `PDF … ${exportPct}%` : "PDF wird vorbereitet …")
                : "PDF exportieren"}
            </PrimaryButton>
          </>
        }
      />

      <div className="px-8 py-8 space-y-6">
        {data.transactions.length === 0 && (
          <div className="bg-[#FFF4E5] border border-[#915000]/30 rounded-md p-5 flex items-start gap-3" data-testid="zero-tx-banner">
            <AlertTriangle className="h-5 w-5 text-[#915000] shrink-0 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1">
              <div className="font-display font-bold text-[#915000]">Keine Buchungen erkannt</div>
              <div className="text-sm text-[#915000]/80 mt-1">
                Die KI konnte in diesem Auszug keine Buchungen finden. Das kann an einem gescannten PDF, ungewöhnlichem Layout oder einer temporären LLM-Störung liegen.
              </div>
              {s.extraction_debug?.error && (
                <div className="text-xs text-[#915000]/70 mt-1 font-mono">Debug: {s.extraction_debug.error}</div>
              )}
              <div className="text-xs text-[#915000]/70 mt-1">
                Verwendete Seiten: {s.extraction_debug?.pages_used ?? "—"}
              </div>
              <div className="mt-3">
                <PrimaryButton onClick={reanalyze} disabled={reanalyzing} data-testid="reanalyze-inline-btn">
                  {reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Jetzt erneut analysieren
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white border border-[#E6E6E4] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Buchungen</div>
            <div className="mt-1 font-display font-extrabold text-2xl">{data.transactions.length}</div>
          </div>
          <div className="bg-white border border-[#E6E6E4] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Zugeordnet</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-[#1E4620]">{matchedCount}</div>
          </div>
          <div className="bg-white border border-[#E6E6E4] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Offen</div>
            <div className="mt-1 font-display font-extrabold text-2xl text-[#915000]">{data.transactions.length - matchedCount}</div>
          </div>
          <div className="bg-white border border-[#E6E6E4] rounded-md p-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">Anfangs- / Endsaldo</div>
            <div className="mt-1 text-sm font-mono-tab">{fmtEUR(s.opening_balance)} → <b>{fmtEUR(s.closing_balance)}</b></div>
          </div>
        </div>

        <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
          <div className="border-b border-[#E6E6E4] px-6 py-4">
            <div className="font-display font-bold text-base text-[#1A1A1A]">Buchungen</div>
            <div className="text-xs text-[#5C5E60] mt-0.5">Belege zuordnen: Klicken Sie unten auf „Beleg zuordnen“ bei einer offenen Buchung.</div>
          </div>
          <table className="w-full text-sm" data-testid="transactions-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8]">
                <th className="text-left px-4 py-2.5 font-semibold w-8">#</th>
                <th className="text-left px-4 py-2.5 font-semibold">Datum</th>
                <th className="text-left px-4 py-2.5 font-semibold">Verwendungszweck</th>
                <th className="text-right px-4 py-2.5 font-semibold">Betrag</th>
                <th className="text-left px-4 py-2.5 font-semibold">Beleg</th>
                <th className="text-right px-6 py-2.5 font-semibold">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((tx) => (
                <tr key={tx.id} className="border-t border-[#E6E6E4] hover:bg-[#F9F9F8] transition-colors duration-150" data-testid={`tx-row-${tx.id}`}>
                  <td className="px-4 py-3 text-[#5C5E60] font-mono-tab">{tx.index + 1}</td>
                  <td className="px-4 py-3 font-mono-tab text-[#1A1A1A]">{fmtDate(tx.booking_date)}</td>
                  <td className="px-4 py-3 max-w-md">
                    <div className="text-[#1A1A1A]">{tx.description}</div>
                    {tx.counterparty && <div className="text-xs text-[#5C5E60] mt-0.5">{tx.counterparty}</div>}
                  </td>
                  <td className={`text-right px-4 py-3 font-mono-tab font-semibold ${tx.amount < 0 ? "text-[#B31D1D]" : "text-[#1E4620]"}`}>
                    {fmtEUR(tx.amount)}
                  </td>
                  <td className="px-4 py-3">
                    {tx.matched_receipt ? (
                      <div className="text-xs">
                        <div className="font-medium text-[#1A1A1A]">{tx.matched_receipt.vendor || tx.matched_receipt.filename}</div>
                        <div className="text-[#5C5E60] font-mono-tab">{fmtEUR(tx.matched_receipt.total_amount)}</div>
                      </div>
                    ) : pendingReqs[tx.id] ? (
                      <StatusBadge variant="info">Anfrage läuft</StatusBadge>
                    ) : (
                      <StatusBadge variant="warning">Beleg fehlt</StatusBadge>
                    )}
                  </td>
                  <td className="text-right px-6 py-3">
                    {tx.matched_receipt ? (
                      <button onClick={() => openUnmatch(tx)}
                        data-testid={`unmatch-${tx.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#5C5E60] transition-colors duration-150">
                        <Unlink className="h-3.5 w-3.5" strokeWidth={1.75} /> Aufheben
                      </button>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        {pendingReqs[tx.id] ? (
                          <button onClick={() => cancelRequest(pendingReqs[tx.id].id)}
                            data-testid={`cancel-request-${tx.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D] transition-colors duration-150"
                            title="Beleg-Anfrage abbrechen">
                            <X className="h-3.5 w-3.5" strokeWidth={1.75} /> Anfrage stoppen
                          </button>
                        ) : (
                          <button
                            onClick={() => openRequest(tx)}
                            disabled={team.length === 0}
                            data-testid={`request-${tx.id}`}
                            title={team.length === 0 ? "Kein Mitarbeiter mit Telegram verknüpft" : "Beleg per Telegram anfordern"}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md transition-colors duration-150 ${team.length === 0 ? "text-[#B1B3B5] cursor-not-allowed" : "hover:bg-[#E4F0E8] text-[#18322B]"}`}>
                            <Send className="h-3.5 w-3.5" strokeWidth={1.75} /> Beleg anfordern
                          </button>
                        )}
                        <select
                          data-testid={`assign-select-${tx.id}`}
                          disabled={assigning === tx.id}
                          onChange={(e) => e.target.value && assign(tx.id, e.target.value)}
                          defaultValue=""
                          className="text-xs px-2 py-1 border border-[#E6E6E4] rounded-md bg-white hover:border-[#18322B] transition-colors duration-150"
                        >
                          <option value="">Beleg zuordnen…</option>
                          {unmatchedReceipts.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.vendor || r.filename} · {fmtEUR(r.total_amount)} · {fmtDate(r.date)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {data.transactions.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-sm text-[#5C5E60]">Keine Buchungen erkannt.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {requestingTx && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setRequestingTx(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="request-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">Beleg per Telegram anfordern</div>
              <button onClick={() => setRequestingTx(null)} className="text-[#5C5E60] hover:text-[#1A1A1A]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="text-xs text-[#5C5E60] bg-[#F9F9F8] rounded p-3">
              <div>{fmtDate(requestingTx.booking_date)} · <b>{fmtEUR(requestingTx.amount)}</b></div>
              <div className="mt-0.5">{requestingTx.description}</div>
              {requestingTx.counterparty && <div className="mt-0.5">{requestingTx.counterparty}</div>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Mitarbeiter</label>
              <select
                value={requestMember}
                onChange={(e) => setRequestMember(e.target.value)}
                data-testid="request-member-select"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]">
                {team.length === 0 && <option value="">Kein Mitarbeiter mit Telegram-Verknüpfung</option>}
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email}{m.telegram_username ? ` (@${m.telegram_username})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Notiz (optional)</label>
              <textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                data-testid="request-note"
                rows={2}
                placeholder="Bitte den Beleg für die Tankfüllung vom Freitag."
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton onClick={() => setRequestingTx(null)}>Abbrechen</SecondaryButton>
              <PrimaryButton onClick={submitRequest} disabled={!requestMember} data-testid="request-submit">
                <Send className="h-4 w-4" /> Anfrage senden
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {unmatchTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => !unmatchBusy && setUnmatchTarget(null)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="unmatch-modal">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-[#1A1A1A]">Zuordnung aufheben</div>
              <button onClick={() => !unmatchBusy && setUnmatchTarget(null)} className="text-[#5C5E60] hover:text-[#1A1A1A]">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="text-xs text-[#5C5E60] bg-[#F9F9F8] rounded p-3 space-y-1">
              <div>
                <span className="font-semibold text-[#1A1A1A]">Buchung:</span>{" "}
                {fmtDate(unmatchTarget.tx.booking_date)} · {fmtEUR(unmatchTarget.tx.amount)}
              </div>
              <div className="truncate">{unmatchTarget.tx.description}</div>
              {unmatchTarget.tx.matched_receipt && (
                <div>
                  <span className="font-semibold text-[#1A1A1A]">Beleg:</span>{" "}
                  {unmatchTarget.tx.matched_receipt.vendor || unmatchTarget.tx.matched_receipt.filename}
                  {" · "}{fmtEUR(unmatchTarget.tx.matched_receipt.total_amount)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">
                Notiz für die KI (optional)
              </label>
              <textarea
                value={unmatchNote}
                onChange={(e) => setUnmatchNote(e.target.value)}
                data-testid="unmatch-note"
                rows={4}
                placeholder="z. B. „Diese Rechnung gehört nicht zu dieser Buchung — Betrag stimmt zwar, aber die Rechnungsnummer passt nicht. Bitte nicht nochmal so zuordnen.“"
                className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
              <div className="text-[11px] text-[#5C5E60] mt-1.5">
                Die KI liest diese Notiz bei zukünftigen Zuordnungen und vermeidet den gleichen Fehler.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <SecondaryButton onClick={() => setUnmatchTarget(null)} disabled={unmatchBusy} data-testid="unmatch-cancel">
                Abbrechen
              </SecondaryButton>
              <PrimaryButton onClick={submitUnmatch} disabled={unmatchBusy} data-testid="unmatch-submit">
                {unmatchBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                Zuordnung aufheben
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
